<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue'
import BaseCard from './BaseCard.vue'
import BaseButton from './BaseButton.vue'

type ConfirmationVariant = 'warning' | 'danger' | 'info'
type ConfirmationSecondaryVariant = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'ghost' | 'link' | 'icon'

const props = withDefaults(defineProps<{
  show?: boolean
  title?: string
  message: string
  confirmText?: string
  cancelText?: string
  secondaryText?: string
  secondaryVariant?: ConfirmationSecondaryVariant
  variant?: ConfirmationVariant
}>(), {
  show: false,
  title: 'Confirm Action',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  secondaryText: '',
  secondaryVariant: 'secondary',
  variant: 'warning',
})

const emit = defineEmits<{
  (event: 'confirm'): void
  (event: 'cancel'): void
  (event: 'secondary'): void
}>()

const isVisible = ref<boolean>(props.show)

watch(
  () => props.show,
  (newVal) => {
    isVisible.value = newVal
  },
)

const handleConfirm = (): void => {
  emit('confirm')
  isVisible.value = false
}

const handleCancel = (): void => {
  emit('cancel')
  isVisible.value = false
}

const handleSecondary = (): void => {
  emit('secondary')
  isVisible.value = false
}

// Close dialog on Escape key
const handleKeydown = (event: KeyboardEvent): void => {
  if (event.key === 'Escape') {
    handleCancel()
  }
}

// Prevent background scroll when dialog is open
watch(isVisible, (newVal) => {
  if (newVal) {
    document.addEventListener('keydown', handleKeydown)
    document.body.style.overflow = 'hidden'
  } else {
    document.removeEventListener('keydown', handleKeydown)
    document.body.style.overflow = ''
  }
})
onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeydown)
  if (isVisible.value) document.body.style.overflow = ''
})
</script>

<template>
  <div
    v-if="isVisible"
    class="fixed inset-0 z-50 flex items-center justify-center confirmation-dialog-overlay bg-black/50 backdrop-blur-sm"
  >
    <BaseCard
      variant="complex"
      class="w-full max-w-md mx-4 confirmation-dialog"
      role="dialog"
      aria-labelledby="confirmation-title"
      aria-describedby="confirmation-message"
    >
      <template #content>
        <div class="confirmation-content">
          <h3
            id="confirmation-title"
            class="mb-2 text-lg font-semibold"
          >
            {{ title }}
          </h3>
          <p
            id="confirmation-message"
            class="mb-6 text-on-surface"
          >
            {{ message }}
          </p>
        </div>
      </template>

      <template #actions>
        <div class="flex flex-wrap justify-end w-full gap-3">
          <BaseButton
            variant="secondary"
            @click="handleCancel"
            class="flex-1"
          >
            {{ cancelText }}
          </BaseButton>
          <BaseButton
            v-if="secondaryText"
            :variant="secondaryVariant"
            @click="handleSecondary"
            class="flex-1"
          >
            {{ secondaryText }}
          </BaseButton>
          <BaseButton
            :variant="variant === 'danger' ? 'danger' : 'primary'"
            @click="handleConfirm"
            class="flex-1"
          >
            {{ confirmText }}
          </BaseButton>
        </div>
      </template>
    </BaseCard>
  </div>
</template>

<style scoped>
.confirmation-dialog-overlay {
  animation: fadeIn 0.2s ease-out;
}

.confirmation-dialog {
  animation: slideIn 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
</style>
